import { Box, Skeleton, useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { utils as commonUtils } from '@finley-technologies/common';

import { useGetUserActiveCreditFacilitiesQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { ProgressAlert } from 'packages/common-frontend/components/Alert';
import { LoanProductSelect } from 'packages/common-frontend/components/LoanProductSelect';
import {
  dashboardRoutePadding,
  errorToastConfig,
  errorToastId,
} from 'packages/common-frontend/constants';
import { useQueryParams } from 'packages/common-frontend/hooks/useQueryParams';
import { creditFacilityIdQueryParam } from 'packages/credit-facility/ui/CreditFacilitySelect';
import { ReceivablesTable } from 'packages/loanProduct/ui/receivablesDashboard/ReceivablesTable';
import { useAthenaSchemaContext } from 'packages/organization/hooks/AthenaSchemaContext';
import {
  PortfolioTabOptionKeys,
  PortfolioTemplate,
} from 'packages/portfolio/ui/shared/PortfolioTemplate';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

export const PortfolioReceivablesView = () => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();
  const query = useQueryParams();
  const {
    AthenaSchemaContextLoadingOrErrorComponent,
  } = useAthenaSchemaContext();

  const [loanProductId, setLoanProductId] = useState<string>('all');

  const creditFacilityId = query.get(creditFacilityIdQueryParam);

  let routeLevelError = false;

  // preferably we do not call this from within packages, as these are meant to be common
  // across admin and borrower / lender dash.  The downstream usage of this
  // "userData" assumes this is never called from admin dash. likely, userData (orgId)
  // needs to passed in from higher scope where orgId can safely be resolved.
  const { data: userData } = useGetUserActiveCreditFacilitiesQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (!userData || !userData.user) {
    return <Skeleton data-testid="loading-receivables" height="600px" />;
  }

  const {
    user: {
      organizationId,
      organization: { creditFacilities },
    },
  } = userData;

  const creditFacility = _.find(
    creditFacilities,
    ({ id }) => id === creditFacilityId,
  );

  const creditFacilitySlug = creditFacility?.slug;

  if (
    !creditFacilityId ||
    !creditFacilitySlug ||
    !creditFacility.latestCreditAgreementId
  ) {
    navigate('/portfolio/overview/facilities');
    toast({
      ...errorToastConfig,
      id: 'errorLoadPortfolioReceivablesNoCreditFacility',
      title: 'Please select a credit facility.',
    });
    return null;
  }

  return (
    <Box width="100%" padding={dashboardRoutePadding}>
      <Sentry.ErrorBoundary
        onError={() => {
          if (!routeLevelError && !toast.isActive(errorToastId)) {
            routeLevelError = true;
            toast({
              ...errorToastConfig,
              id: 'errorLoadReceivables',
              title: 'Error loading portfolio receivables route',
            });
            navigate('/');
          }
        }}
      >
        <AthenaSchemaContextLoadingOrErrorComponent
          renderLoading={
            <Box>
              <ProgressAlert>
                Getting your portfolio ready. This may take a moment.
              </ProgressAlert>
            </Box>
          }
        >
          <PortfolioTemplate
            organizationId={organizationId}
            creditFacilitySlug={creditFacilitySlug}
            selectedTab={PortfolioTabOptionKeys.receivables}
          >
            <Box w="50%">
              <LoanProductSelect
                creditFacilityId={creditFacilityId}
                loanProductId={loanProductId}
                setLoanProductId={setLoanProductId}
                useAllOption
              />
            </Box>

            <ReceivablesTable
              organizationId={organizationId}
              creditFacilitySlug={creditFacilitySlug}
              creditFacilityId={creditFacilityId}
              loanProductId={loanProductId ?? undefined}
            />
          </PortfolioTemplate>
        </AthenaSchemaContextLoadingOrErrorComponent>
      </Sentry.ErrorBoundary>
    </Box>
  );
};
